import { MouseEvent, useState } from "react";
import { Prompt } from "react-router";
import { generatePath, useHistory } from "react-router-dom";
import { Form, Formik } from "formik";
import { isEmpty, isEqual } from "lodash";
import classNames from "classnames";
import * as Yup from "yup";
import { observer } from "mobx-react";

import Avatar from "components/base/avatar/avatar";
import Button from "components/base/button/button";
import DropdownList from "components/base/dropdown-list/dropdown-list";
import InputField from "components/base/input-field/input-field";
import Toggler from "components/base/toggler/toggler";
import Tooltip from "components/base/tooltip/tooltip";
import UploadTarget from "components/base/upload-target/upload-target";
import { cropImageHandler, ICroppedFileOptions } from "components/partial/image-cropper/image-cropper.helper";
import { acceptedMimeTypes } from "constants/constants";
import { errorMessages, infoMessages } from "constants/messages";
import { IOptionsItem } from "helpers/select.helper";
import { getAvatarTitle } from "helpers/string.helper";
import { descriptionValidator, lightValidationSchema, onlyNumberValidator } from "helpers/validator.helper";
import { IFileModel } from "models/dto/ZoomiLxp/Models/Common/IFileModel";
import { ICreateGroupModel } from "models/dto/ZoomiLxp/Models/GroupModels/ICreateGroupModel";
import { IGroupModel } from "models/dto/ZoomiLxp/Models/GroupModels/IGroupModel";
import { IUpdateGroupModel } from "models/dto/ZoomiLxp/Models/GroupModels/IUpdateGroupModel";
import { FileTypes } from "models/dto/ZoomiLxp/Utilities/Enumerations/FileTypes";
import { GroupThresholdMode } from "models/dto/ZoomiLxp/Utilities/Enumerations/GroupThresholdMode";
import { UploadFileTypes } from "models/dto/ZoomiLxp/Utilities/Enumerations/UploadFileTypes";
import { useAppStore } from "store";
import { ReactComponent as InfoIcon } from "assets/icons/ic_info.svg";
import { GroupTabs } from "components/partial/submenu/items";
import { Routes } from "routes";
import styles from "./styles.module.scss";

type ThresholdToggleFieldName =
	| "enableExamThreshold"
	| "enableZ1Threshold"
	| "enableEngagementThreshold"
	| "enableProgressThreshold";
type ThresholdValueFieldName = "examThreshold" | "z1Threshold" | "engagementThreshold" | "progressThreshold";
type ThresholdModeFieldName =
	| "examThresholdMode"
	| "z1ThresholdMode"
	| "engagementThresholdMode";

interface Props {
	group: IGroupModel;
	onSaved: (group: IGroupModel) => void;
	isNewGroup?: boolean;
}

const eachCourseOption: IOptionsItem = { value: GroupThresholdMode.PerCourse, label: "Each course" };
const averageAcrossCoursesOption: IOptionsItem = {
	value: GroupThresholdMode.AverageAcrossAllCourses,
	label: "Average across courses",
};
const applyToOptions: IOptionsItem[] = [eachCourseOption, averageAcrossCoursesOption];
const standardGroupTypeOption: IOptionsItem = { value: "grp__", label: "Standard" };
const licenseGroupTypePrefix = "licensegrp__";
const visibilityGroupTypePrefix = "visgrp__";
const groupTypeOptions: IOptionsItem[] = [
	standardGroupTypeOption,
	{ value: licenseGroupTypePrefix, label: "Restricted" },
	{ value: visibilityGroupTypePrefix, label: "Visibility" },
];
const groupTypeTooltipText =
	"Standard groups grant group members access to all courses in the group but also allow the courses to be found in search and purchased by users who do not belong to a group. Restricted groups allow their courses to be found in search but they can only be accessed by group members. Visibility groups hide courses from anyone who is not a member of the group.";

const getGroupTypePrefix = (groupName?: string): string =>
	groupTypeOptions.find((option) => groupName?.startsWith(String(option.value)))?.value.toString() ??
	standardGroupTypeOption.value.toString();

const getGroupSettingsSchema = (groupTypePrefix: string) => {
	const isLicenseGroup = groupTypePrefix === licenseGroupTypePrefix;
	const licensePriceError = "The price is required and should be higher than 50 cents";
	const basePriceSchema = Yup.number()
		.typeError("You must specify a number")
		.when({
			is: (price: number) => price > 0,
			then: Yup.number()
				.min(
					50,
					isLicenseGroup
						? "The price is required and should be higher than 50 cents"
						: "The price should be 0 (for free) or higher than 50 cents."
				)
				.required(infoMessages.requiredField),
		});
	const licensePriceSchema = Yup.number()
		.typeError(licensePriceError)
		.required(licensePriceError)
		.min(50, licensePriceError);

	return Yup.object().shape({
		name: lightValidationSchema(
			"Please check if group name filled correctly.",
			"The group name length must be less than 500 symbols.",
			500
		),
		displayName: lightValidationSchema(
			"Please check if display name filled correctly.",
			"The display name length must be less than 500 symbols.",
			500
		),
		description: descriptionValidator.required(infoMessages.requiredField),
		thumbnail: Yup.object().nullable().required("Thumbnail is required"),
		price: isLicenseGroup ? licensePriceSchema : basePriceSchema,
		examThreshold: Yup.number().nullable().min(0, "Invalid value").max(100, "Invalid value"),
		z1Threshold: Yup.number().nullable().min(0, "Invalid value").max(100, "Invalid value"),
		engagementThreshold: Yup.number().nullable().min(0, "Invalid value").max(100, "Invalid value"),
		progressThreshold: Yup.number().nullable().min(0, "Invalid value").max(100, "Invalid value"),
	});
};

const getInitialValues = (group: IGroupModel): IGroupModel => ({
	...group,
	price: group.price ?? 0,
	examThresholdMode: group.examThresholdMode ?? GroupThresholdMode.PerCourse,
	z1ThresholdMode: group.z1ThresholdMode ?? GroupThresholdMode.PerCourse,
	engagementThresholdMode: group.engagementThresholdMode ?? GroupThresholdMode.PerCourse,
});

const getThresholdModeOption = (mode?: GroupThresholdMode): IOptionsItem =>
	applyToOptions.find((option) => option.value === mode) ?? eachCourseOption;

const getGroupPayload = (group: IGroupModel, isVisibilityGroup: boolean): IUpdateGroupModel => ({
	displayName: group.displayName,
	description: group.description,
	...(!isVisibilityGroup && { price: group.price }),
	thumbnailFileId: group.thumbnail?.id,
	autoAssignCourses: group.autoAssignCourses,
	discoverableForLearners: isVisibilityGroup ? false : group.discoverableForLearners,
	enableExamThreshold: group.enableExamThreshold,
	examThreshold: group.enableExamThreshold ? group.examThreshold ?? undefined : undefined,
	examThresholdMode: group.examThresholdMode,
	enableZ1Threshold: group.enableZ1Threshold,
	z1Threshold: group.enableZ1Threshold ? group.z1Threshold ?? undefined : undefined,
	z1ThresholdMode: group.z1ThresholdMode,
	enableEngagementThreshold: group.enableEngagementThreshold,
	engagementThreshold: group.enableEngagementThreshold ? group.engagementThreshold ?? undefined : undefined,
	engagementThresholdMode: group.engagementThresholdMode,
	enableProgressThreshold: group.enableProgressThreshold,
	progressThreshold: group.enableProgressThreshold ? group.progressThreshold ?? undefined : undefined,
});

const GroupSettings = ({ group, onSaved, isNewGroup = false }: Props) => {
	const { groupStore, toasterStore } = useAppStore();
	const history = useHistory();
	const [isPictureLoading, setIsPictureLoading] = useState<boolean>(false);
	const [selectedGroupType, setSelectedGroupType] = useState<IOptionsItem>(standardGroupTypeOption);
	const selectedGroupTypePrefix = selectedGroupType.value.toString();
	const groupTypePrefix = isNewGroup ? selectedGroupTypePrefix : getGroupTypePrefix(group.name);
	const isLicenseGroup = groupTypePrefix === licenseGroupTypePrefix;
	const isVisibilityGroup = groupTypePrefix === visibilityGroupTypePrefix;

	const fileCroppingOptions: ICroppedFileOptions = {
		imageType: FileTypes.Thumbnail,
		uploadFileType: UploadFileTypes.CourseThumbnail,
	};

	const getThresholdValue = (value?: number | string | null) => value?.toString() ?? "";
	const changePrice = (value: string, onChange: (value: number) => void) => {
		if (value === "") {
			onChange(0);
			return;
		}

		if (onlyNumberValidator(value)) {
			onChange(Number(value));
		}
	};
	const onSave = async (values: IGroupModel) => {
		try {
			if (isNewGroup) {
				const createGroupData: ICreateGroupModel = {
					...getGroupPayload(values, isVisibilityGroup),
					prefix: selectedGroupTypePrefix,
					name: values.name,
				};
				const createdGroup = await groupStore.addGroup(createGroupData);
				if (createdGroup) {
					onSaved(createdGroup);
					toasterStore.showSuccessMessage("New group successfully created");
					history.push(generatePath(Routes.CmsGroupProfileTab, { id: createdGroup.id, tab: GroupTabs.Settings }));
				}
			} else {
				const updatedGroup = await groupStore.updateGroup(values.id, getGroupPayload(values, isVisibilityGroup));
				if (updatedGroup) {
					onSaved(updatedGroup);
					toasterStore.showSuccessMessage("Group data successfully updated");
				}
			}
		} catch (err) {
			toasterStore.showErrorMessage(err);
		}
	};

	return (
		<Formik
			initialValues={getInitialValues(group)}
			validationSchema={getGroupSettingsSchema(groupTypePrefix)}
			onSubmit={() => {}}
			validateOnChange={false}
			enableReinitialize
		>
			{({ values, errors, setFieldValue, validateForm, setErrors }) => {
				const isValuesChanged = !isEqual(getInitialValues(group), values);
				const isSavingDisabled = groupStore.isLoading || !isValuesChanged;
				const saveGroupSettings = async (e: MouseEvent<HTMLDivElement>) => {
					e.preventDefault();
					const validationErrors = await validateForm();
					if (isEmpty(validationErrors)) {
						await onSave(values);
					} else {
						setErrors(validationErrors);
						toasterStore.showErrorMessage("Please check is form filled correctly.");
					}
				};
				const changeThresholdToggle = (
					toggleFieldName: ThresholdToggleFieldName,
					value: boolean
				) => {
					setFieldValue(toggleFieldName, value);
				};
				const changeThresholdValue = (fieldName: ThresholdValueFieldName, value: string) => {
					if (value === "") {
						setFieldValue(fieldName, null);
						return;
					}

					const numericValue = Number(value);
					if (!Number.isNaN(numericValue) && numericValue >= 0 && numericValue <= 100) {
						setFieldValue(fieldName, numericValue);
					}
				};
				const changeThresholdMode = (fieldName: ThresholdModeFieldName, option: IOptionsItem | null) => {
					if (option) {
						setFieldValue(fieldName, option.value);
					}
				};
				const groupName = values.name?.trim() ?? "";
				const groupTag = `${selectedGroupType.value}${groupName}`;

				return (
					<Form>
						<Prompt message={infoMessages.leaveWithoutSaving} when={!isNewGroup && isValuesChanged} />
						<div className={styles.group_settings__header}>
							<p className={styles.group_settings__header_title}>{isNewGroup ? "Details" : group.displayName}</p>
							<Button
								label="Save"
								onClick={saveGroupSettings}
								className={styles.group_settings__save_button}
								disabled={isSavingDisabled}
								testId="group_settings__save_button"
							/>
						</div>
						<div className={styles.group_settings__tile}>
							{!isNewGroup && (
								<div className={styles.group_settings__tile_control}>
									<div className={styles.group_settings__togglers_container}>
										<div className={styles.group_settings__toggler_group}>
											<Tooltip
												label={
													"All courses from the group will be automatically assigned to learners after joining the group."
												}
												className={styles.group_settings__tooltip}
											>
												<InfoIcon />
											</Tooltip>
											<span className={styles.group_settings__toggler_label}>Auto-assign Courses</span>
											<Toggler
												value={values.autoAssignCourses}
												onChange={(value: boolean) => setFieldValue("autoAssignCourses", value)}
												className={styles.group_settings__toggler}
											/>
										</div>
										<div className={styles.group_settings__toggler_group}>
											{!isVisibilityGroup && (
												<>
													<Tooltip
														label={
															"When a group is discoverable, users will be able to find it via the Groups carousel and in search. Groups not set to discoverable will appear only to their members and to admins in the CMS."
														}
														className={styles.group_settings__tooltip}
													>
														<InfoIcon />
													</Tooltip>
													<span className={styles.group_settings__toggler_label}>Discoverable for Learners</span>
													<Toggler
														value={values.discoverableForLearners}
														onChange={(value: boolean) => setFieldValue("discoverableForLearners", value)}
														className={styles.group_settings__toggler}
													/>
												</>
											)}
										</div>
									</div>
								</div>
							)}
							<div
								className={classNames(styles.group_settings__content_container, {
									[styles.group_settings__content_container_new]: isNewGroup,
									[styles.group_settings__content_container_no_price]: isVisibilityGroup,
								})}
							>
								<div className={styles.group_settings__image_upload_container}>
									<UploadTarget
										accept={acceptedMimeTypes.pictures}
										onSuccess={(res, type) => {
											cropImageHandler({
												parentFile: { ...res.data, contentType: type },
												filesOptions: fileCroppingOptions,
												onCropSuccessHandler: (newFile: IFileModel) => setFieldValue("thumbnail", newFile),
												onLoading: (isLoading) => setIsPictureLoading(isLoading),
												aspect: 16 / 9,
											});
										}}
										onError={(err) => console.log(err)}
										onLoading={(isLoading) => setIsPictureLoading(isLoading)}
										maxSize={10}
										onWrongFormat={(type) =>
											toasterStore.showErrorMessage(errorMessages.unsupportedFileType(type, "a JPG or PNG image"))
										}
										onMaxSizeExceeded={(size, maxSize) =>
											toasterStore.showErrorMessage(errorMessages.exceedingAllowedFileSize(size, maxSize))
										}
										fileType={UploadFileTypes.CourseThumbnail}
									>
										<Avatar
											title={getAvatarTitle(values.displayName || values.name, "")}
											size="medium"
											className={styles.group_settings__thumbnail}
											image={values.thumbnail?.url}
											isLoading={isPictureLoading}
										>
											<div className={styles.group_settings__image_cover}>
												{values.thumbnail?.url ? "Change cover" : "+ Add cover"}
											</div>
										</Avatar>
										{!!errors.thumbnail && (
											<p className={styles.group_settings__thumbnail_message}>{errors.thumbnail}</p>
										)}
									</UploadTarget>
								</div>
								{isNewGroup && (
									<div className={styles.group_settings__group_name_wrapper}>
										<div className={styles.group_settings__prefix_container}>
											<DropdownList
												placeholder="Group Type"
												options={groupTypeOptions}
												value={selectedGroupType}
												onChange={(option) => {
													if (option) {
														const nextGroupTypePrefix = option.value.toString();
														setSelectedGroupType(option);
														if (nextGroupTypePrefix === visibilityGroupTypePrefix) {
															setFieldValue("price", 0);
															setFieldValue("discoverableForLearners", false);
														} else if (values.price == null) {
															setFieldValue("price", 0);
														}
													}
												}}
												className={styles.group_settings__prefix_dropdown}
											/>
											<Tooltip label={groupTypeTooltipText} className={styles.group_settings__prefix_tooltip}>
												<InfoIcon />
											</Tooltip>
										</div>
										<InputField
											inputType="text"
											label="Group Name"
											className={styles.group_settings__form_field}
											name="name"
											value={values.name ?? ""}
											onChange={(e) => setFieldValue("name", e.target.value)}
											isError={!!errors.name}
											errorText={errors.name}
										/>
									</div>
								)}
								<InputField
									inputType="text"
									label="Group Tag"
									className={classNames(styles.group_settings__form_field, styles.group_settings__title_field, {
										[styles.group_settings__form_field_error]: !!errors.name,
									})}
									name="groupTag"
									value={isNewGroup ? groupTag : values.name ?? ""}
									onChange={() => {}}
									isError={!isNewGroup && !!errors.name}
									errorText={!isNewGroup ? errors.name : undefined}
									disabled
								/>
								<InputField
									inputType="text"
									label="Display Name *"
									className={classNames(styles.group_settings__form_field, styles.group_settings__display_name_field, {
										[styles.group_settings__form_field_error]: !!errors.displayName,
									})}
									name="displayName"
									value={values.displayName ?? ""}
									onChange={(e) => setFieldValue("displayName", e.target.value)}
									isError={!!errors.displayName}
									errorText={errors.displayName}
								/>
								{!isVisibilityGroup && (
									<InputField
										inputType="text"
										label={`Price (in cents)${isLicenseGroup ? " *" : ""}`}
										className={classNames(styles.group_settings__form_field, styles.group_settings__price_field, {
											[styles.group_settings__price_field_error]: !!errors.price,
										})}
										name="price"
										value={values.price?.toString() ?? "0"}
										onChange={(e) => changePrice(e.target.value, (value) => setFieldValue("price", value))}
										isError={!!errors.price}
										errorText={errors.price}
										inputMode="numeric"
									/>
								)}
								<InputField
									fieldType="textarea"
									label="Description *"
									className={classNames(styles.group_settings__input_area, {
										[styles.group_settings__form_field_error]: !!errors.description,
										[styles.group_settings__input_area_error]: !!errors.description,
									})}
									name="description"
									value={values.description ?? ""}
									onChange={(e) => setFieldValue("description", e.target.value)}
									isError={!!errors.description}
									errorText={errors.description}
									maxLength={10000}
								/>
								{!isNewGroup && (
									<div className={styles.group_settings__certification}>
										<div className={styles.group_settings__certification_header}>
											<span className={styles.group_settings__certification_title}>Certification</span>
											<Tooltip
												label={
													"When a group is a certification group, learner progress is tracked against configured completion thresholds."
												}
												className={styles.group_settings__certification_tooltip}
											>
												<InfoIcon />
											</Tooltip>
										</div>
										<div className={styles.group_settings__certification_rows}>
											<div className={styles.group_settings__threshold_row}>
												<div className={styles.group_settings__threshold_toggle}>
													<Toggler
														value={values.enableExamThreshold}
														onChange={(value: boolean) =>
															changeThresholdToggle("enableExamThreshold", value)
														}
														className={styles.group_settings__toggler}
													/>
													<span className={styles.group_settings__threshold_label}>Exam threshold</span>
												</div>
												<div className={styles.group_settings__threshold_controls}>
													<DropdownList
														placeholder="Apply to"
														options={applyToOptions}
														value={getThresholdModeOption(values.examThresholdMode)}
														onChange={(option) => changeThresholdMode("examThresholdMode", option)}
														className={styles.group_settings__threshold_dropdown}
														isDisabled={!values.enableExamThreshold}
													/>
													<InputField
														inputType="text"
														label="0-100"
														className={styles.group_settings__threshold_input}
														name="examThreshold"
														value={getThresholdValue(values.examThreshold)}
														onChange={(e) => changeThresholdValue("examThreshold", e.target.value)}
														isError={!!errors.examThreshold}
														errorText={errors.examThreshold}
														disabled={!values.enableExamThreshold}
													/>
												</div>
											</div>
											<div className={styles.group_settings__threshold_row}>
												<div className={styles.group_settings__threshold_toggle}>
													<Toggler
														value={values.enableZ1Threshold}
														onChange={(value: boolean) =>
															changeThresholdToggle("enableZ1Threshold", value)
														}
														className={styles.group_settings__toggler}
													/>
													<span className={styles.group_settings__threshold_label}>Z1 threshold</span>
												</div>
												<div className={styles.group_settings__threshold_controls}>
													<DropdownList
														placeholder="Apply to"
														options={applyToOptions}
														value={getThresholdModeOption(values.z1ThresholdMode)}
														onChange={(option) => changeThresholdMode("z1ThresholdMode", option)}
														className={styles.group_settings__threshold_dropdown}
														isDisabled={!values.enableZ1Threshold}
													/>
													<InputField
														inputType="text"
														label="0-100"
														className={styles.group_settings__threshold_input}
														name="z1Threshold"
														value={getThresholdValue(values.z1Threshold)}
														onChange={(e) => changeThresholdValue("z1Threshold", e.target.value)}
														isError={!!errors.z1Threshold}
														errorText={errors.z1Threshold}
														disabled={!values.enableZ1Threshold}
													/>
												</div>
											</div>
											<div className={styles.group_settings__threshold_row}>
												<div className={styles.group_settings__threshold_toggle}>
													<Toggler
														value={values.enableEngagementThreshold}
														onChange={(value: boolean) =>
															changeThresholdToggle("enableEngagementThreshold", value)
														}
														className={styles.group_settings__toggler}
													/>
													<span className={styles.group_settings__threshold_label}>Engagement threshold</span>
												</div>
												<div className={styles.group_settings__threshold_controls}>
													<DropdownList
														placeholder="Apply to"
														options={applyToOptions}
														value={getThresholdModeOption(values.engagementThresholdMode)}
														onChange={(option) => changeThresholdMode("engagementThresholdMode", option)}
														className={styles.group_settings__threshold_dropdown}
														isDisabled={!values.enableEngagementThreshold}
													/>
													<InputField
														inputType="text"
														label="0-100"
														className={styles.group_settings__threshold_input}
														name="engagementThreshold"
														value={getThresholdValue(values.engagementThreshold)}
														onChange={(e) => changeThresholdValue("engagementThreshold", e.target.value)}
														isError={!!errors.engagementThreshold}
														errorText={errors.engagementThreshold}
														disabled={!values.enableEngagementThreshold}
													/>
												</div>
											</div>
											<div className={styles.group_settings__threshold_row}>
												<div className={styles.group_settings__threshold_toggle}>
													<Toggler
														value={values.enableProgressThreshold}
														onChange={(value: boolean) =>
															changeThresholdToggle("enableProgressThreshold", value)
														}
														className={styles.group_settings__toggler}
													/>
													<span className={styles.group_settings__threshold_label}>Progress threshold</span>
												</div>
												<div
													className={classNames(
														styles.group_settings__threshold_controls,
														styles.group_settings__threshold_controls_full_width
													)}
												>
													<InputField
														inputType="text"
														label="0-100"
														className={styles.group_settings__threshold_input}
														name="progressThreshold"
														value={getThresholdValue(values.progressThreshold)}
														onChange={(e) => changeThresholdValue("progressThreshold", e.target.value)}
														isError={!!errors.progressThreshold}
														errorText={errors.progressThreshold}
														disabled={!values.enableProgressThreshold}
													/>
												</div>
											</div>
										</div>
									</div>
								)}
							</div>
						</div>
					</Form>
				);
			}}
		</Formik>
	);
};

export default observer(GroupSettings);

